/*
 * Copyright (c) Castle Point Learning Systems Corporation, 2013-2023, all rights reserved.
 * NOTICE: Dissemination of the information and/or materials contained in this file is strictly
 * forbidden unless prior written permission is obtained from Castle Point Learning Systems Corporation.
 * See LICENSE.txt (part of this package) for the full notice.
 */
import { Status, } from '../../../types/model';
import { PLACEHOLDER } from '../../constants';
import { EquivalenceSign } from '../../../types/rest-client';
import { CursorSide, enclosingRect, HorizontalDirection, MathEquality, MathEqualityType, MathNodeName, traverseStructures, traverseSymbols, } from 'semantic-math-editor';
import { toGRL } from '../../../types/grl/GRL';
import { SolutionConverter } from '../../SolutionConverter';
import { mathClient } from '../../../api/MathClient';
export function localId() {
    return s4() + s4();
}
function s4() {
    return Math.floor((1 + Math.random()) * 0x10000)
        .toString(16)
        .substring(1);
}
export function calculateParts(tree) {
    const parts = [];
    const equalities = [];
    traverseStructures(tree, (node) => {
        if (node.name === MathNodeName.equality) {
            const equality = node;
            if (equality.right.name !== MathNodeName.equality) {
                parts.unshift(equality.right);
            }
            equalities.unshift(equality);
            if (equality.left.name !== MathNodeName.equality) {
                parts.unshift(equality.left);
            }
        }
    });
    if (parts.length === 0 && equalities.length === 0) {
        parts.push(tree);
    }
    return parts;
}
export function calculateEqualities(tree) {
    const parts = [];
    const equalities = [];
    traverseStructures(tree, (node) => {
        if (node.name === MathNodeName.equality) {
            const equality = node;
            if (equality.right.name !== MathNodeName.equality) {
                parts.unshift(equality.right);
            }
            equalities.unshift(equality);
            if (equality.left.name !== MathNodeName.equality) {
                parts.unshift(equality.left);
            }
        }
    });
    return equalities;
}
export function calcEqualityOffset(line) {
    const editor = line.editor;
    const parts = calculateParts(line.tree);
    const equalities = calculateEqualities(line.tree);
    if (parts[parts.length - 2]) {
        const editorRect = editor.element.getBoundingClientRect();
        const firstEqualityRect = getEqualityRect(equalities[0], editor);
        const lastEqualityRect = getEqualityRect(equalities[equalities.length - 1], editor);
        return {
            ...line,
            firstEqualityX: firstEqualityRect.left - editorRect.left,
            lastEqualityX: lastEqualityRect.left - editorRect.left,
        };
    }
    else {
        return { ...line, firstEqualityX: 0, lastEqualityX: 0 };
    }
}
function getEqualityRect(node, editor) {
    const rects = editor
        .getDOMElement(node.symbols.symbol.nodeId)
        .filter((element) => !!element)
        .map((element) => element.getBoundingClientRect());
    return enclosingRect(...rects);
}
export function alignLines(lines) {
    const shifts = lines.reduce((previousValue, currentValue, index, array) => {
        if (index > 0) {
            const previousLine = array[index - 1];
            const currentLine = array[index];
            const relativeShift = previousLine.lastEqualityX - currentLine.firstEqualityX;
            const absoluteShift = relativeShift + previousValue[index - 1];
            return [...previousValue, absoluteShift];
        }
        return previousValue;
    }, [0]);
    const equalities = calculateEqualities(lines[0].tree);
    if (lines.length > 1 && equalities.length === 0) {
        shifts[0] = 20;
    }
    const minShift = Math.min(...shifts);
    lines.forEach((line, index) => {
        line.shift = shifts[index] - minShift;
    });
    return lines;
}
export function getPreviousLine(evaluation, lineId) {
    const index = evaluation.lines.findIndex((l) => l.id === lineId);
    if (index <= 0) {
        return;
    }
    return evaluation.lines[index - 1];
}
export function getNextLine(evaluation, lineId) {
    const index = evaluation.lines.findIndex((l) => l.id === lineId);
    if (index === -1 || index === evaluation.lines.length - 1) {
        return;
    }
    return evaluation.lines[index + 1];
}
export function isEmptyTree(tree) {
    return tree.name === MathNodeName.placeholder;
}
export function createEquality(leftPart, rightPart, equality) {
    const equalityType = equality ? equality.equalityType : MathEqualityType.equals;
    const left = leftPart ? { ...leftPart } : PLACEHOLDER;
    const right = rightPart ? { ...rightPart } : PLACEHOLDER;
    return new MathEquality(equalityType, left, right);
}
export function phonyEquality(rightPart, equality) {
    return createEquality(undefined, rightPart, equality);
}
export function findPartByNodes(line, nodeIds) {
    return calculateParts(line.tree).find((part) => {
        let found = false;
        traverseSymbols(part, (symbol) => {
            if (nodeIds.includes(symbol.nodeId)) {
                found = true;
            }
        });
        return found;
    });
}
export function findNodesByTree(tree) {
    const nodeIds = [];
    traverseSymbols(tree, (symbol) => {
        nodeIds.push(symbol.nodeId);
    });
    return nodeIds;
}
export function findCurrentPartIndex(line) {
    const nodeIds = getNodeIdsNearCursor(line.editor);
    const parts = calculateParts(line.tree);
    const index = parts.findIndex((part) => {
        let found = false;
        traverseSymbols(part, (symbol) => {
            if (!found && nodeIds.includes(symbol.nodeId)) {
                found = true;
            }
        });
        return found;
    });
    if (index !== -1) {
        return index;
    }
    return parts.findIndex((part) => {
        let found = false;
        traverseStructures(part, (structure) => {
            const symbol = structure.symbols;
            if (!found && symbol.hasOwnProperty('symbol')) {
                const mathSymbol = symbol.symbol;
                if (line.editor.getSymbolsAtCursor(true).includes(mathSymbol.nodeId)) {
                    found = true;
                }
            }
        });
        return found;
    });
}
export function getNodeIdsNearCursor(editor) {
    const cursor = editor.getCursor();
    const nodeIds = [];
    if (cursor.rightNode) {
        nodeIds.push(cursor.rightNode.nodeId);
    }
    if (cursor.leftNode) {
        nodeIds.push(cursor.leftNode.nodeId);
    }
    return nodeIds;
}
export function getNearestNode(editor, point) {
    let newPoint = point;
    const rect = editor.element.getBoundingClientRect();
    if (rect.top > point.y) {
        newPoint = { x: point.x, y: rect.top };
    }
    if (rect.bottom < point.y) {
        newPoint = { x: point.x, y: rect.bottom };
    }
    return editor.getNearestNode(newPoint);
}
export function getRightPosition(editor) {
    const rect = editor.element.getBoundingClientRect();
    return editor.getNearestNode({ x: rect.x + rect.width, y: rect.top });
}
export function getLeftPosition(editor) {
    const rect = editor.element.getBoundingClientRect();
    return editor.getNearestNode({ x: rect.x, y: rect.top });
}
export function getSelectionBoundaryPoint(editor, activeBoundary = true) {
    const boundingRect = editor.hasSelection() ? editor.getSelectionBoundingRect()[0] : editor.getCursorBoundingRect();
    const leftBoundary = { x: boundingRect.left, y: boundingRect.top };
    const rightBoundary = { x: boundingRect.right, y: boundingRect.bottom };
    if (activeBoundary) {
        return editor.getSelectionHorizontalDirection() === HorizontalDirection.right ? rightBoundary : leftBoundary;
    }
    else {
        return editor.getSelectionHorizontalDirection() === HorizontalDirection.right ? leftBoundary : rightBoundary;
    }
}
export function isLastLine(line, evaluation) {
    return evaluation.lines.indexOf(line) === evaluation.lines.length - 1;
}
export function lastPart(line) {
    const parts = calculateParts(line.tree);
    if (parts.length === 0) {
        return undefined;
    }
    return parts[parts.length - 1];
}
export function convertToStoreModel(evaluation) {
    return {
        id: evaluation.id,
        lines: evaluation.lines.map((l) => ({ id: l.id, tree: l.tree })),
    };
}
export function retrieveSemanticAssist(evaluation, skip) {
    return evaluation.lines
        .map((l) => {
        if (!l.editor) {
            return [];
        }
        const parts = calculateParts(l.tree);
        const startsWithPlaceholder = parts.length > 0 && parts[0].name === MathNodeName.placeholder;
        return l.editor
            .getSemantic()
            .errors.slice(startsWithPlaceholder ? 1 : 0)
            .map((e) => {
            const part = findPartByNodes(l, e.nodeIds);
            const partIndex = parts.indexOf(part);
            return {
                lineId: l.id,
                partIndex,
                partNodeIds: findNodesByTree(part),
                affectedParts: [partIndex],
                accurateNodeIds: e.nodeIds,
                message: { lastLevelRevealsStep: true, content: [{ level: 0, text: [e.description] }] },
                status: 'error',
                visible: true,
            };
        })
            .filter((e) => !(skip && skip.lineId === e.lineId && skip.partIndex === e.partIndex && parts[skip.partIndex].name === MathNodeName.placeholder));
    })
        .flatMap((e) => e);
}
export function toExtendedEvaluation(evaluation) {
    return {
        id: evaluation.id,
        lines: evaluation.lines.map((l) => {
            const parts = calculateParts(l.tree);
            const errors = l.editor ? l.editor.getSemantic().errors.map((e) => findPartByNodes(l, e.nodeIds)) : [];
            return {
                ...l,
                parts,
                mathElements: parts.map((p) => ({
                    id: Math.random() * 999999,
                    status: errors.includes(p) ? Status.error : Status.ok,
                    sign: EquivalenceSign.equal,
                    expression: toGRL(p),
                })),
            };
        }),
    };
}
export function placeCursor(editor) {
    const tree = editor.getTree();
    if (tree.name === MathNodeName.equality) {
        const parts = calculateParts(tree);
        if (parts[0].name === MathNodeName.placeholder) {
            const nodeId = findNodesByTree(parts[1])[0];
            if (nodeId) {
                editor.placeCursor(nodeId, CursorSide.left);
            }
            else {
                const cursor = getRightPosition(editor);
                editor.placeCursor(cursor.nodeId, cursor.side);
            }
        }
        else {
            const cursor = getLeftPosition(editor);
            editor.placeCursor(cursor.nodeId, cursor.side);
        }
    }
    else {
        const cursor = getRightPosition(editor);
        editor.placeCursor(cursor.nodeId, cursor.side);
    }
}
export function getPartIndexForSkip(prev, curr) {
    if (prev.partsCount !== curr.partsCount && prev.partsCount < curr.partsCount) {
        return { lineId: curr.lineId, partIndex: curr.activePartIndex };
    }
}
export function decorateEditorPlaceholder(html) {
    const element = html.querySelector('[katex-id="N"]');
    if (element && element.childNodes) {
        element.childNodes.forEach((c) => {
            const elem = c;
            elem.style.width = '6px';
            element.style.boxShadow = '0 0 1px';
        });
    }
}
export async function fecthMathAssist(evaluation, solutionId, problem, setMathInterpretation) {
    const extendedEvaluation = toExtendedEvaluation(evaluation);
    const { expressionToParts, solution } = SolutionConverter.toMathSolution(solutionId, extendedEvaluation);
    const assist = await mathClient.getAssist(solution, problem);
    setMathInterpretation({ solution, assist });
    return assist.rows
        .flatMap((r) => r.elements)
        .map((e) => {
        const relatedParts = expressionToParts.get(e.elementId);
        const lineId = extendedEvaluation.lines.find((l) => l.mathElements.some((e) => relatedParts.includes(e.id))).id;
        const line = extendedEvaluation.lines.find((l) => l.id === lineId);
        const partIndexes = line.mathElements.reduce((p, c, i) => (relatedParts.includes(c.id) ? p.concat(i) : p), []);
        let partNodeIds;
        let affectedParts = [];
        if (partIndexes.length === 1) {
            affectedParts.push(partIndexes[0]);
            partNodeIds = findNodesByTree(line.parts[affectedParts[0]]);
        }
        else {
            affectedParts.push(Math.min(...partIndexes));
            affectedParts.push(Math.max(...partIndexes));
            partNodeIds = findNodesByTree(createEquality(line.parts[affectedParts[0]], line.parts[affectedParts[1]]));
        }
        return partIndexes.reduce((p, partIndex) => {
            return p.concat({
                lineId,
                partIndex: partIndex,
                partNodeIds,
                affectedParts,
                accurateNodeIds: partNodeIds,
                messageCode: e.messageCode,
                messageParameters: e.messageParameters,
                status: e.status,
                visible: e.visible,
            });
        }, []);
    })
        .flatMap((arr) => arr);
}
export function computeHash(evaluation) {
    return evaluation.lines.map((l) => toGRL(l.tree).toString()).join();
}
export function applyAssistFiltration(assist, lineId, activePartIndex) {
    const forCurrentLine = assist.filter((e) => e.lineId === lineId);
    const tail = forCurrentLine.filter((e) => e.partIndex >= activePartIndex);
    const affectedParts = tail.map((e) => e.affectedParts).flatMap((a) => a);
    const excludePartIndexes = affectedParts.concat(tail.map((a) => a.partIndex));
    const exclude = forCurrentLine.filter((a) => excludePartIndexes.includes(a.partIndex));
    return assist.filter((e) => e.lineId !== lineId || !exclude.includes(e));
}
